import {PaginationParameters} from "@api/ApiModels";
import {WalletReward} from "@lib/Wallet/WalletModels";
import {CatalogProduct, CatalogProductSpecificType} from "@lib/Catalog/CatalogModels";

export interface RedemptionFilters extends PaginationParameters {
    initiativeFormat?: string[];
    initiativeCode?: string;
    status?: string;
    from?: string;
    to?: string;
}

export enum RedemptionStatusEnum {
    INSERTED = 'INSERTED',
    NOT_ACCEPTED = 'NOT_ACCEPTED',
    NOT_APPROVED = 'NOT_APPROVED',
    WAITING = 'WAITING',
    WAITING_FOR_BACKOFFICE = 'WAITING_FOR_BACKOFFICE',
    WAITING_FOR_USER = 'WAITING_FOR_USER',
    COMPLETED = 'COMPLETED',
    DELETED = 'DELETED',
    EXPIRED = 'EXPIRED'
}

export const RedemptionStatusColors: Record<RedemptionStatusEnum, string> = {
    [RedemptionStatusEnum.INSERTED]: "neutral",          // grigio / info
    [RedemptionStatusEnum.NOT_ACCEPTED]: "error",       // rosso
    [RedemptionStatusEnum.NOT_APPROVED]: "error",       // rosso
    [RedemptionStatusEnum.WAITING]: "warning",          // giallo/arancio
    [RedemptionStatusEnum.WAITING_FOR_BACKOFFICE]: "warning",
    [RedemptionStatusEnum.WAITING_FOR_USER]: "warning", // pending user action
    [RedemptionStatusEnum.COMPLETED]: "success",        // verde
    [RedemptionStatusEnum.DELETED]: "error",            // rosso (o neutral-muted se preferisci)
    [RedemptionStatusEnum.EXPIRED]: "error",            // rosso
};

export type REDEMPTION_STATUS_KEYS = keyof typeof RedemptionStatusEnum;

export enum ApprovalTypeEnum {
    NONE = 'NONE',
    LIGHT = 'LIGHT',
    STANDARD = 'STANDARD'
}

export enum IssueTypeEnum {
    ADDRESS_PENDING = 'ADDRESS_PENDING',
    APPROVAL_PENDING = 'APPROVAL_PENDING',
    BACKOFFICE_APPROVAL_PENDING = 'BACKOFFICE_APPROVAL_PENDING',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
    PDA_VALIDATION_PENDING = 'PDA_VALIDATION_PENDING'
}

export const IsWaitingForUserStatuses: RedemptionStatusEnum[] = [
    RedemptionStatusEnum.WAITING_FOR_BACKOFFICE,
    RedemptionStatusEnum.WAITING_FOR_USER
];

export const IsWaitingForUserIssues: IssueTypeEnum[] = [
    IssueTypeEnum.APPROVAL_PENDING,
    IssueTypeEnum.PAYMENT_PENDING,
    IssueTypeEnum.ADDRESS_PENDING
];

export enum OrderStatusEnum {
    INSERTED = 'INSERTED',
    NOT_APPROVED = 'NOT_APPROVED',
    APPROVED = 'APPROVED',
    STOCK_ISSUE = 'STOCK_ISSUE',
    ISSUE = 'ISSUE',
    SENT = 'SENT',
    DELIVERED = 'DELIVERED',
    ANNULLED = 'ANNULLED',
    DELETED = 'DELETED',
    EXPIRED = 'EXPIRED'
}

export const OrderStatusColors: Record<OrderStatusEnum, string> = {
    [OrderStatusEnum.INSERTED]: "neutral",
    [OrderStatusEnum.NOT_APPROVED]: "error",
    [OrderStatusEnum.APPROVED]: "success",
    [OrderStatusEnum.STOCK_ISSUE]: "error",
    [OrderStatusEnum.ISSUE]: "error",
    [OrderStatusEnum.SENT]: "success",
    [OrderStatusEnum.DELIVERED]: "success",
    [OrderStatusEnum.ANNULLED]: "error",
    [OrderStatusEnum.DELETED]: "error",
    [OrderStatusEnum.EXPIRED]: "error",
};

export type ORDER_STATUS_KEYS = keyof typeof OrderStatusEnum;

export enum DeliveryStatusEnum {
    SENT = 'SENT',
    DELIVERED = 'DELIVERED',
    ANNULLED = 'ANNULLED',
    DYING = 'DYING',
    ISSUE = 'ISSUE'
}

export enum DocumentTypeEnum {
    ID_CARD = 'ID_CARD',
    PASSPORT = 'PASSPORT',
    DRIVING_LICENSE = 'DRIVING_LICENSE'
}

export interface Redemption {
    logicId: string;
    friendlyId: string;
    dateTime: string;
    status: RedemptionStatusEnum;
    address?: Address;
    referredInitiativeLogicId: string;
    initiativeFormat: string;
    initiativeName: string;
    totals?: Totals;
    approvalRequested: ApprovalTypeEnum;
    orderItems?: OrderItem[];
    issues: Issue[];
    orders?: Order[];
    approvalDetail?: ApprovalDetail;
    couriers?: Courier[];
}

export interface RedemptionItem extends OrderItem {
    redemption: Redemption;
    orders?: Order[];
}

export interface Totals {
    requiredPoints?: Omit<WalletReward, "referredEventId" | "eventType">[];
    additionalCost?: AdditionalCost;
}

export interface AdditionalCost {
    value: string;
    currency: string;
}

export interface ApprovalDetail {
    pdfLink?: string;
    phone?: string;
    documentCode: string;
    documentImages: DocumentImage[];
    documentType: DocumentTypeEnum;
    fl_acceptance: boolean;
    birthdate?: string;
    birthplace?: string;
    approval_date?: string;
    address?: Address;
    delivery_address?: Address;
}

export interface DocumentImage {
    image?: string;
}

export interface Courier {
    logicId?: string;
    externalId?: string;
    description?: string;
    partners?: CourierPartner[];
}

export interface CourierPartner {
    partnerId?: string;
    courierId?: string;
    nationCode?: string;
    flScheduledDelivery?: boolean;
}

export interface Issue {
    logicId: string;
    externalId?: string;
    issueType: IssueTypeEnum;
    note?: string;
}

export interface Order {
    logicId: string;
    friendlyId: string;
    orderStatus: OrderStatusEnum;
    date: string;
    address: Address;
    products: OrderItem[];
    ordersTracking?: OrderTracking[];
    deliveries?: Delivery[];
}

export interface OrderItem {
    orderItemLogicId: string;
    product: CatalogProduct;
    quantity: number;
    value?: ItemValue;
    vouchers?: Voucher[];
    amount: number;
    referredWalletConfigurationLogicId?: string;
    cashAmount?: number;
    itemType: CatalogProductSpecificType;
}

export interface ItemValue {
    wallets?: Omit<WalletReward, "referredEventId" | "eventType">[];
    additionalCost?: AdditionalCost;
}

export interface Voucher {
    customData?: string | null;
    content?: string | null;
    expiryDateVoucher?: string | null;
    expiryDateLink?: string | null;
}

export interface DeliveryTrackingDto {
    customData?: string | null;
    logicId?: string | null;
    status?: string | null;
    operator?: string | null;
    opCode?: string | null;
    note?: string | null;
    creationDate?: string | null;
}

export interface RedemptionModality {
    logicId?: string;
    pointsPrice?: number;
    discountPoint?: number;
    selfPoints?: number;
    discountSelfPoints?: number;
    selfCash?: number;
    discountSelfCash?: number;
    sellPrice?: number;
    discountSellPrice?: number;
    walletConfigurationLogicId?: string;
}

export interface Address {
    logicId?: string;
    name?: string;
    surname?: string;
    businessName?: string;
    email?: string;
    street?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
    careOf?: string;
    note?: string;
    phone?: string;
}

export interface OrderTracking {
    logicId?: string;
    status: OrderStatusEnum;
    operator: string;
    opCode: string;
    note?: string;
    creationDate: string;
}

export interface Delivery {
    customData?: string | null;
    logicId?: string | null;
    externalId?: string | null;
    friendlyId?: string | null;
    orderLogicId?: string | null;
    billingId?: string | null;
    trackingLink?: string | null;
    status?: string | null;
    previousStatus?: string | null;
    shippingId?: string | null;
    shippingCost?: number | null;
    name?: string | null;
    surname?: string | null;
    email?: string | null;
    street?: string | null;
    town?: string | null;
    province?: string | null;
    postalCode?: string | null;
    country?: string | null;
    note?: string | null;
    deliveriesTrackingDto?: DeliveryTrackingDto[] | null;
}

/**
 * Redemptions requests
 */
export interface RedemptionAddress {
    name?: string | null;
    surname?: string | null;
    street?: string | null;
    streetNumber?: string | null;
    postalCode?: string | null;
    nation?: string | null;
    province?: string | null;
    city?: string | null;
    note?: string | null;

    careOf?: string | null;
    region?: string | null;
    latitude?: string | null;
    longitude?: string | null;
}

export interface RedemptionDeliveryAddress {
    name?: string | null;
    surname?: string | null;
    businessName?: string | null;
    email?: string | null;
    street?: string | null;
    city?: string | null;
    province?: string | null;
    postalCode?: string | null;
    country?: string | null;
    careOf?: string | null;
    phone?: string | null;
    note?: string | null;
}

export interface RedemptionRequest {
    email: string;
    name?: string | null;
    surname?: string | null;
    phone?: string | null;
    address?: RedemptionAddress;
}

export interface ApprovalRequest {
    fl_acceptance: boolean;
    birthdate?: string| null;
    birthplace?: string|null;
    address: RedemptionDeliveryAddress;
    delivery_address: RedemptionDeliveryAddress;
    additionalTags?: Record<string, any>;
    documentType?: string | null;
    documentCode?: string | null;
    documentImages?: { image: string; }[];
    document_issuing_body?: string | null;
    document_issue_date?: string | null;
    document_expire_date?: string | null;
}

/**
 * UI
 */
export interface RedemptionRowVm {
    id: string;
    friendlyId: string;
    dateTime: string;

    productTitle: string;
    productImage?: string;
    quantity: number;

    pointsSpent: number;

    status: RedemptionStatusEnum;
    orderStatus?: OrderStatusEnum;

    redemption: Redemption;
    orderItems: OrderItem[];
}

export type OrderTimelineLinkVm = {
    type: "voucher" | "tracking";
    label: string;
    href: string;
}

export type TimelineTone = "neutral" | "progress" | "success" | "danger";

export type OrderTimelineStepVm = {
    id: string;
    status: OrderStatusEnum;
    dateTime: string;
    label: string;
    description: string;
    note?: string | null;
    links: OrderTimelineLinkVm[];
    tone: TimelineTone;
    isCurrent: boolean;
    isTerminal: boolean;
}

export const ORDER_LINK_ATTACHMENT_BY_ITEM_TYPE: Partial<
    Record<CatalogProductSpecificType, OrderStatusEnum>
> = {
    [CatalogProductSpecificType.VOUCHER]: OrderStatusEnum.DELIVERED,
    [CatalogProductSpecificType.ITEM]: OrderStatusEnum.SENT,
}